import {useCallback, useEffect, useRef, type KeyboardEvent} from 'react';

type UseHoverOrFocusDisclosureOptions = {
  /** Sustained-hover delay before `onOpen` fires. */
  openDelayMs?: number;
  /** Grace period before `onClose` fires, so the pointer can travel the gap
   *  between the trigger and a portal-rendered flyout without flickering. */
  closeDelayMs?: number;
  /**
   * Whether focus opens immediately rather than going through the same
   * debounce as mouse hover. Defaults to true — keyboard users shouldn't
   * have to wait out a hover-intent delay meant to filter accidental
   * mouse-overs. Set false when the open action is itself expensive/animated
   * and should share the hover cadence instead of firing on every tab stop.
   */
  focusOpensImmediately?: boolean;
};

/**
 * Shared hover-or-focus disclosure logic for portal-rendered flyouts
 * (collapsed-sidebar quick-nav flyouts, object-picker type flyouts,
 * conversation context cards). Handles the open/close debounce timers,
 * Escape-to-close, and the mouse↔keyboard parity every one of these sites
 * needs: React's `onFocus`/`onBlur` are backed by `focusin`/`focusout`, which
 * (unlike native `focus`/`blur`) bubble — so a single pair of flyout-level
 * handlers covers focus landing on any descendant, exactly mirroring the
 * mouseenter/mouseleave bridge between a trigger and its flyout.
 *
 * `onOpen`/`onClose` own the actual reveal (position calculation, visibility
 * state, any gating like "don't open while a context menu is open") — this
 * hook only owns the timing and event wiring around calling them.
 */
export function useHoverOrFocusDisclosure(
  onOpen: () => void,
  onClose: () => void,
  options: UseHoverOrFocusDisclosureOptions = {}
) {
  const {
    openDelayMs = 180,
    closeDelayMs = 160,
    focusOpensImmediately = true,
  } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const scheduleOpen = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(onOpen, openDelayMs);
  }, [clearTimer, onOpen, openDelayMs]);

  const scheduleClose = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(onClose, closeDelayMs);
  }, [clearTimer, onClose, closeDelayMs]);

  /** Cancel a pending close — call on entering the flyout itself. */
  const keepOpen = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearTimer();
        onClose();
      }
    },
    [clearTimer, onClose]
  );

  return {
    /** Spread onto the trigger element (icon, row, button). */
    triggerHandlers: {
      onMouseEnter: scheduleOpen,
      onMouseLeave: scheduleClose,
      onFocus: focusOpensImmediately ? onOpen : scheduleOpen,
      onBlur: scheduleClose,
      onKeyDown: handleKeyDown,
    },
    /** Spread onto the portal-rendered flyout (and the bridge element, if any). */
    flyoutHandlers: {
      onMouseEnter: keepOpen,
      onMouseLeave: scheduleClose,
      onFocus: keepOpen,
      onBlur: scheduleClose,
      onKeyDown: handleKeyDown,
    },
    keepOpen,
  };
}
